import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { db, isFirebaseConfigured } from '../firebase';
import { doc, setDoc, onSnapshot, getDoc } from 'firebase/firestore';

export interface SiteSettings {
  creditCardLimit: string;
  shippingNJ: string;
  shippingCA: string;
  shippingTX: string;
  customsBrokerFee: string;
  duty: string;
  storageOutdoor: string;
  storageIndoorShared: string;
  storageIndoorExclusive: string;
  taxCanadianResidents: string;
  taxNonResidentsLocalPickup: string;
  taxNonResidentsShipped: string;
}

const DEFAULTS: SiteSettings = {
  creditCardLimit: '$3500',
  shippingNJ: 'USD 1700',
  shippingCA: 'USD 2600',
  shippingTX: 'USD 2100',
  customsBrokerFee: 'USD 600',
  duty: '2.5% of Purchase price',
  storageOutdoor: 'CAD 60',
  storageIndoorShared: 'CAD 200',
  storageIndoorExclusive: 'CAD 250',
  taxCanadianResidents: '13% of Purchase Price',
  taxNonResidentsLocalPickup: '13% of Purchase Price',
  taxNonResidentsShipped: 'No Tax',
};

const LOCAL_STORAGE_KEY = 'eurojdm_site_settings';
const SETTINGS_UPDATED_EVENT = 'eurojdm:settings-updated';

const loadSettingsFromLocalStorage = (): SiteSettings => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored) {
    try { return { ...DEFAULTS, ...JSON.parse(stored) }; } catch { /* fall through */ }
  }
  return DEFAULTS;
};

interface SiteSettingsContextType {
  settings: SiteSettings;
  updateSettings: (updates: Partial<SiteSettings>) => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextType | null>(null);

export const SiteSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(loadSettingsFromLocalStorage);

  /* ── Firestore real-time sync ── */
  useEffect(() => {
    if (!isFirebaseConfigured || !db) return;

    const settingsRef = doc(db, 'settings', 'site');

    const seedIfNeeded = async () => {
      const snap = await getDoc(settingsRef);
      if (!snap.exists()) {
        await setDoc(settingsRef, DEFAULTS);
      }
    };
    seedIfNeeded().catch(console.error);

    const unsubscribe = onSnapshot(settingsRef, (snap) => {
      if (snap.exists()) {
        const data = { ...DEFAULTS, ...snap.data() } as SiteSettings;
        setSettings(data);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
      }
    }, (error) => {
      console.error('Firestore settings sync error:', error);
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── localStorage cross-tab sync (fallback mode) ── */
  useEffect(() => {
    if (isFirebaseConfigured) return;

    const syncFromStorage = () => {
      setSettings(loadSettingsFromLocalStorage());
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOCAL_STORAGE_KEY || e.key === null) syncFromStorage();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncFromStorage();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', syncFromStorage);
    window.addEventListener(SETTINGS_UPDATED_EVENT, syncFromStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', syncFromStorage);
      window.removeEventListener(SETTINGS_UPDATED_EVENT, syncFromStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const updateSettings = useCallback(async (updates: Partial<SiteSettings>) => {
    if (isFirebaseConfigured && db) {
      const settingsRef = doc(db, 'settings', 'site');
      await setDoc(settingsRef, updates, { merge: true });
    } else {
      setSettings(prev => {
        const next = { ...prev, ...updates };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
        window.dispatchEvent(new CustomEvent(SETTINGS_UPDATED_EVENT));
        return next;
      });
    }
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = (): SiteSettingsContextType => {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) throw new Error('useSiteSettings must be used within SiteSettingsProvider');
  return ctx;
};
